import type { IContainer } from '@monofe/di';
import { buildBindable } from '@monofe/di';
import { injectConfigAPI, TYPES as UTIL_TYPES } from '@monofe/util';
import {
    injectAuthManager,
    injectMediaManager,
    injectUserStateManager,
    TYPES as CONTROLLER_TYPES,
} from '../../controller/interface/bind';
import { injectWorkspaceDB, TYPES as DB_TYPES } from '../../db/interface/bind';
import { bindAPISession, injectAPISession, TYPES, unbindAPISession } from '../interface/bind';
import type { ISession, ISessionArgs, ISessionInstantiator } from '../interface/session';
import sessionInst from './session';
import sessionBuilder from './session.build';
import sessionSerialize from './session.serialize';

function _build(container: IContainer): Readonly<ISessionInstantiator> {
    const authInst = injectAuthManager(container);
    const mediaInst = injectMediaManager(container);
    const userInst = injectUserStateManager(container);
    const dbInst = injectWorkspaceDB(container);
    const configAPI = injectConfigAPI(container);

    const sessionObject: ISessionInstantiator = {
        ...sessionInst,
        restore: (dataStr: string) => {
            const sessionData = sessionSerialize.expand(dataStr);
            return sessionBuilder.buildFromSessionData(
                authInst,
                mediaInst,
                userInst,
                dbInst,
                sessionData,
            );
        },
        serialize: (sessionIn: ISession) => Promise.resolve(sessionSerialize.flatten(sessionIn)),
        open: (name: string, args?: ISessionArgs) =>
            sessionInst.open(configAPI, authInst, mediaInst, userInst, dbInst, name, args),
        exists: (name: string) => sessionInst.exists(name),
    };

    return Object.freeze(sessionObject);
}

export default buildBindable<ISessionInstantiator>({
    id: TYPES.ISessionInstantiator,
    dependencies: [
        CONTROLLER_TYPES.IAuthenticationManager,
        CONTROLLER_TYPES.IMediaManager,
        CONTROLLER_TYPES.IUserStateManager,
        DB_TYPES.IWorkspaceDBInstantiate,
        UTIL_TYPES.IConfigAPI,
    ],
    bind: bindAPISession,
    remove: unbindAPISession,
    inject: injectAPISession,
    build: container => Promise.resolve(_build(container)),
});
